import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ChatService} from '../chat/chat.service';
import {Subject, takeUntil} from 'rxjs';
import {SubscriptionUtils} from '../../commons/subscription.utils';
import {ZyggioService} from './zyggio.service';
import {AuthStorage} from '../../auth/auth-storage';
import {ConversationEntry} from './model/conversation.entry';
import {MessageEntry} from './model/message.entry';
import {NotificationService} from '../../notification/notification.service';

@Component({
  selector: 'app-zyggio-window',
  templateUrl: './zyggio.component.html',
  styleUrls: ['./zyggio.component.scss']
})
export class ZyggioComponent implements OnInit, OnDestroy {

  @ViewChild('messageList') private messageListRef: ElementRef;

  protected viewMode: 'list' | 'conversation' = 'list';
  protected conversations: ConversationEntry[] = [];
  protected messages: MessageEntry[] = [];
  protected inputText = '';
  protected isStreaming = false;
  protected streamingText = '';

  private sseBuffer = '';
  private zyggioState = 'out';
  private conversationPageSize = 20;
  private hasMoreConversations = true;
  private isLoadingConversations = false;
  private currentConversationId: number | null = null;
  private subSubject = new Subject<boolean>();
  private abortController: AbortController | null = null;

  constructor(private chatService: ChatService,
              private zyggioService: ZyggioService,
              private notificationService: NotificationService,
              private cd: ChangeDetectorRef) {
  }

  public ngOnInit(): void {
    this.chatService.toggleZyggio()
      .pipe(takeUntil(this.subSubject))
      .subscribe({
        next: (state) => {
          this.zyggioState = state ? 'in' : 'out';
          this.addGlobalClass();
          if (this.zyggioState === 'in') {
            this.onShowList();
          }
        }
      });
  }

  public onShowList(): void {
    this.viewMode = 'list';
    this.conversations = [];
    this.hasMoreConversations = true;
    this.loadMoreConversations();
  }

  public onScrollConversations(event: Event): void {
    if (this.hasMoreConversations && !this.isLoadingConversations) {
      const element = event.target as HTMLElement;
      const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;
      if (nearBottom) {
        this.loadMoreConversations();
      }
    }
  }

  public onSelectConversation(conversation: ConversationEntry): void {
    this.currentConversationId = conversation.id;
    this.viewMode = 'conversation';
    this.streamingText = '';
    this.zyggioService.loadMessages(conversation.id)
      .pipe(takeUntil(this.subSubject))
      .subscribe(messages => {
        this.messages = messages;
        setTimeout(() => this.scrollToBottom());
      });
  }

  public onNewConversation(): void {
    this.currentConversationId = null;
    this.messages = [];
    this.streamingText = '';
    this.viewMode = 'conversation';
  }

  public onDeleteConversation(conversation: ConversationEntry, event: Event): void {
    event.stopPropagation();
    this.zyggioService.deleteConversation(conversation.id)
      .pipe(takeUntil(this.subSubject))
      .subscribe(() => {
        this.conversations = this.conversations.filter(c => c.id !== conversation.id);
      });
  }

  public onSend(): void {
    if (this.inputText.trim() && !this.isStreaming) {
      const message = this.inputText.trim();
      this.inputText = '';
      this.messages = [...this.messages, {role: 'user', content: message}];
      this.isStreaming = true;
      this.streamingText = '';
      this.streamChat(message);
      setTimeout(() => this.scrollToBottom());
    }
  }

  public onStop(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.finalizeStream();
  }

  public onClose(): void {
    this.chatService.triggerZyggioToggle(false);
  }

  public onCopy(content: string): void {
    navigator.clipboard.writeText(content);
  }

  public onFeedback(messageIndex: number, feedback: string): void {
    if (this.currentConversationId) {
      this.zyggioService.saveFeedback(this.currentConversationId, messageIndex, feedback)
        .pipe(takeUntil(this.subSubject))
        .subscribe();
    }
  }

  public onRetry(): void {
    if (this.messages.length >= 1) {
      const lastUserMessage = [...this.messages].reverse().find(m => m.role === 'user');
      if (lastUserMessage) {
        this.messages = this.messages.slice(0, this.messages.length - 1);
        if (this.messages.length > 0 && this.messages[this.messages.length - 1].role === 'assistant') {
          this.messages = this.messages.slice(0, this.messages.length - 1);
        }
        this.isStreaming = true;
        this.streamingText = '';
        this.streamChat(lastUserMessage.content);
      }
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  private loadMoreConversations(): void {
    this.isLoadingConversations = true;
    this.zyggioService.loadConversations(this.conversationPageSize, this.conversations.length)
      .pipe(takeUntil(this.subSubject))
      .subscribe({
        next: (data) => {
            this.conversations = [...this.conversations, ...data];
            this.hasMoreConversations = data.length === this.conversationPageSize;
            this.isLoadingConversations = false;
        },
        error: () => this.notificationService.loadingError()
      })
  }

  private streamChat(message: string): void {
    this.sseBuffer = '';
    this.abortController = new AbortController();
    const token = AuthStorage.accessToken();

    let url = `/api/agent-chat/chat`;
    if (this.currentConversationId) {
      url += `?conversationId=${this.currentConversationId}`;
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'text/plain'
      },
      body: message,
      signal: this.abortController.signal
    }).then(response => {
      if (!response.ok) {
        this.finalizeStream();
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        this.readStream(reader, decoder);
      }
    }).catch(() => this.finalizeStream());
  }

  private readStream(reader: ReadableStreamDefaultReader<Uint8Array>, decoder: TextDecoder): void {
    reader.read().then(({done, value}) => {
      if (done) {
        this.sseBuffer = '';
        this.finalizeStream();
      } else {
        this.sseBuffer += decoder.decode(value, {stream: true});
        const lines = this.sseBuffer.split('\n');
        this.sseBuffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data:')) {
            const payload = line.startsWith('data: ') ? line.substring(6) : line.substring(5);
            this.handleSSEEvent(JSON.parse(payload));
          }
        }
        this.readStream(reader, decoder);
      }
    }).catch(() => {
      this.sseBuffer = '';
      this.finalizeStream();
    });
  }

  private handleSSEEvent(data: any): void {
    if (data.type === 'start') {
      this.currentConversationId = data.conversationId;
    } else if (data.type === 'token') {
      this.streamingText += data.token;
    } else if (data.type === 'done') {
      this.finalizeStream();
    } else if (data.type === 'title') {
      this.updateConversationTitle(data.title);
    } else if (data.type === 'error') {
      this.finalizeStream();
    }
    this.cd.detectChanges();
    this.scrollToBottom();
  }

  private finalizeStream(): void {
    if (this.streamingText) {
      this.messages = [...this.messages, {role: 'assistant', content: this.streamingText}];
      this.streamingText = '';
    }
    this.isStreaming = false;
    this.abortController = null;
    this.cd.detectChanges();
  }

  private updateConversationTitle(title: string): void {
    if (this.currentConversationId) {
      this.conversations = this.conversations.map(c =>
        c.id === this.currentConversationId ? {...c, title} : c
      );
    }
  }

  private scrollToBottom(): void {
    if (this.messageListRef) {
      const el = this.messageListRef.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  private addGlobalClass(): void {
    let bodyClassList = document.body.classList;
    if (this.zyggioState === 'in') {
      if (bodyClassList.contains('main--chat')) {
        bodyClassList.remove('main--chat');
      }
      if (bodyClassList.contains('main--events')) {
        bodyClassList.remove('main--events');
      }
      if (!bodyClassList.contains('main--zyggio')) {
        bodyClassList.add('main--zyggio');
      }
    } else if (this.zyggioState === 'out') {
      bodyClassList.remove('main--zyggio');
    }
  }

  public ngOnDestroy(): void {
    this.onStop();
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
  }

}
